import { alert } from '@/services/alert';
import { ContentType, createReference, getIdentifier, getReferenceString, WithId } from '@medplum/core';
import { useHealthGorillaLabOrder } from '@medplum/health-gorilla-react';
import type { LabOrganization, TestCoding } from '@medplum/health-gorilla-core';
import type {
  Communication,
  Patient,
  Practitioner,
  Questionnaire,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  Resource,
  ResourceType,
  ServiceRequest,
} from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useEffectEvent, useReducer, useRef, useState } from 'react';
import { COMMUNICATION_CATEGORY_CODING_MAP, RESTRICTED_CONFIDENTIALITY_LEVEL_CODING } from '@bariendo/lib/codings';
import { formatHealthGorillaError, validatePatientProfile, validateProviderProfile } from '../labOrderValidation';
import {
  CommunicationCategory,
  HEALTHGORILLA_LABCORP_ACCOUNT_NUMBER_IDENTIFIER_SYSTEM,
  HEALTHGORILLA_SEND_LAB_ORDER_BOT_IDENTIFIER,
  HEALTHGORILLA_TENANT_IDENTIFIER_SYSTEM,
  LAB_ORDER_QUESTIONNAIRE_IDENTIFIER,
} from '@bariendo/lib/constants';
import { BARIENDO_TEST_GROUP_ITEM_EXTENSION } from '@bariendo/lib/extensions';
import { OBESITY_CODING, QUESTIONNAIRE_LINK_IDS } from '../constants';
import { parseTestEntry } from '../parseTestEntry';
import { searchLabOrderTestCodings } from '../searchLabOrderTestCodings';

const HGDX_LABCORP_SEARCH_TERM = 'HGDX LabCorp';
const LABCORP_SEARCH_TERM = 'LabCorp';
const BARIENDO_PRODUCTION_LABCORP_ACCOUNT_NUMBER = 'TA018018';
const BARIENDO_STAGING_LABCORP_ACCOUNT_NUMBER = '87659430';

/**
 * Sets the questionnaire's requester answer to the ordering provider. Written at submit because each
 * QuestionnaireForm block rebuilds the response from its own items, so an answer seeded at load
 * does not reliably survive editing.
 */
export function withRequesterAnswer(
  items: QuestionnaireResponseItem[] | undefined,
  orderingProvider: Practitioner
): QuestionnaireResponseItem[] {
  const requesterItem: QuestionnaireResponseItem = {
    linkId: QUESTIONNAIRE_LINK_IDS.REQUESTER,
    answer: [{ valueReference: createReference(orderingProvider) }],
  };
  const rest = (items ?? []).filter((item) => item.linkId !== QUESTIONNAIRE_LINK_IDS.REQUESTER);
  return [requesterItem, ...rest];
}

function getBariendoLabCorpAccountNumber(): string {
  if (import.meta.env.MODE !== 'production') {
    return BARIENDO_STAGING_LABCORP_ACCOUNT_NUMBER;
  }
  return BARIENDO_PRODUCTION_LABCORP_ACCOUNT_NUMBER;
}

interface LabOrderState {
  questionnaire: Questionnaire | undefined;
  questionnaireResponse: QuestionnaireResponse | undefined;
  isSubmitting: boolean;
  isLoadingQuestionnaire: boolean;
}

type LabOrderAction =
  | { type: 'reset' }
  | { type: 'setQuestionnaire'; questionnaire: Questionnaire }
  | { type: 'setQuestionnaireResponse'; response: QuestionnaireResponse }
  | { type: 'setSubmitting'; isSubmitting: boolean }
  | { type: 'setLoadingQuestionnaire'; isLoading: boolean };

const initialLabOrderState: LabOrderState = {
  questionnaire: undefined,
  questionnaireResponse: undefined,
  isSubmitting: false,
  isLoadingQuestionnaire: true,
};

function labOrderReducer(state: LabOrderState, action: LabOrderAction): LabOrderState {
  switch (action.type) {
    case 'reset':
      return initialLabOrderState;
    case 'setQuestionnaire':
      return { ...state, questionnaire: action.questionnaire };
    case 'setQuestionnaireResponse':
      return { ...state, questionnaireResponse: action.response };
    case 'setSubmitting':
      return { ...state, isSubmitting: action.isSubmitting };
    case 'setLoadingQuestionnaire':
      return { ...state, isLoadingQuestionnaire: action.isLoading };
    default:
      return state;
  }
}

interface UseLabOrderFormProps {
  patient: Patient | undefined;
  /** The signed-in user placing the order: author of the QuestionnaireResponse and sender of the lab-order card. */
  placedBy: Practitioner;
  /** The provider the order is placed under (ServiceRequest.requester). Needs an NPI; may differ from `placedBy` (BAR-2845). */
  orderingProvider: Practitioner | undefined;
  onSuccess: () => void;
}

export function useLabOrderForm({ patient, placedBy, orderingProvider, onSuccess }: UseLabOrderFormProps) {
  const medplum = useMedplum();

  // Use Medplum Health Gorilla integration
  const labOrder = useHealthGorillaLabOrder({
    patient,
    requester: orderingProvider,
  });

  const [state, dispatch] = useReducer(labOrderReducer, initialLabOrderState);
  const [isAutoSelectingTests, setIsAutoSelectingTests] = useState(false);
  const [hasAutoSelectedLab, setHasAutoSelectedLab] = useState(false);
  const [defaultLabId, setDefaultLabId] = useState<string | undefined>();
  const [initialDefaultTests, setInitialDefaultTests] = useState<TestCoding[] | undefined>();
  const [hasUserClearedAllTests, setHasUserClearedAllTests] = useState(false);
  /** Dedupes lab test lookup warnings when auto-select re-runs for the same lab + search name. */
  const warnedMissingTestLookupKeysRef = useRef<Set<string>>(new Set());

  // Initialize billing information when questionnaire loads
  const initializeBillingInfo = useEffectEvent(() => {
    if (!labOrder.state.billingInformation.billTo) {
      labOrder.updateBillingInformation({ billTo: 'customer-account' });
    }
    labOrder.setDiagnoses([
      {
        coding: [OBESITY_CODING],
        text: 'Obesity',
      },
    ]);
  });

  const getHealthGorillaTenantAccountId = useEffectEvent(async () => {
    const tenant = await medplum.searchOne('Organization', {
      identifier: `${HEALTHGORILLA_TENANT_IDENTIFIER_SYSTEM}|`,
    });

    if (!tenant) {
      return getBariendoLabCorpAccountNumber();
    }

    const accountNumber = getIdentifier(tenant, HEALTHGORILLA_LABCORP_ACCOUNT_NUMBER_IDENTIFIER_SYSTEM);

    if (!accountNumber) {
      return getBariendoLabCorpAccountNumber();
    }

    return accountNumber;
  });

  // Load questionnaire
  useEffect(() => {
    async function fetchQuestionnaire() {
      try {
        dispatch({ type: 'setLoadingQuestionnaire', isLoading: true });
        const results = await medplum.searchResources('Questionnaire', {
          status: 'active',
          identifier: LAB_ORDER_QUESTIONNAIRE_IDENTIFIER,
        });
        const preferred = results.find((q) => q.name === 'lab-order-general');
        const q = preferred ?? results?.[0];
        if (q) {
          dispatch({ type: 'setQuestionnaire', questionnaire: q });
          // Initialize response with patient and requester
          const initialResponse: QuestionnaireResponse = {
            resourceType: 'QuestionnaireResponse',
            status: 'in-progress',
            authored: new Date().toISOString(),
            source: createReference(placedBy),
            subject: patient ? createReference(patient) : undefined,
            questionnaire: q.url || (q.id ? getReferenceString(q) : undefined),
            // The requester answer is filled in from the ordering provider at submit (withRequesterAnswer).
            item: [
              {
                linkId: 'patient',
                answer: patient ? [{ valueReference: createReference(patient) }] : undefined,
              },
              {
                linkId: 'diagnoses',
                answer: [{ valueCoding: OBESITY_CODING }],
              },
            ],
          };
          dispatch({ type: 'setQuestionnaireResponse', response: initialResponse });

          // Initialize billing information
          initializeBillingInfo();
        }
      } catch (error) {
        alert.error(`Failed to load lab order form: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
        dispatch({ type: 'setLoadingQuestionnaire', isLoading: false });
      }
    }

    fetchQuestionnaire();
    // initializeBillingInfo is a useEffectEvent - intentionally not in dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medplum, patient, placedBy]);

  // Auto-select default performing lab based on environment (HGDX LabCorp on non-production, LabCorp on production)
  useEffect(() => {
    const performingLab = labOrder.state.performingLab;

    // Only auto-select if no lab is set yet, questionnaire is loaded, and we haven't already auto-selected
    if (performingLab || !state.questionnaire || hasAutoSelectedLab) {
      return;
    }

    let cancelled = false;

    const autoSelectDefaultLab = async () => {
      try {
        // Treat non-production as the default (local, staging, etc.)
        const isNonProduction = import.meta.env.MODE !== 'production';
        const preferredSearchTerm = isNonProduction ? HGDX_LABCORP_SEARCH_TERM : LABCORP_SEARCH_TERM;

        const preferredLabs = await labOrder.searchAvailableLabs(preferredSearchTerm);
        const preferredSearchTermLower = preferredSearchTerm.toLowerCase();

        let labCandidate =
          preferredLabs?.find((lab) => lab.name && lab.name.toLowerCase().includes(preferredSearchTermLower)) ??
          preferredLabs?.[0];

        // Fallback to generic LabCorp search if HGDX LabCorp is not found in non-production
        if (!labCandidate && isNonProduction) {
          const fallbackLabs = await labOrder.searchAvailableLabs(LABCORP_SEARCH_TERM);
          labCandidate =
            fallbackLabs?.find(
              (lab) => lab.name && lab.name.toLowerCase().includes(LABCORP_SEARCH_TERM.toLowerCase())
            ) ?? fallbackLabs?.[0];
        }

        if (!labCandidate || cancelled) {
          return;
        }

        labOrder.setPerformingLab(labCandidate);

        const labAccountNumber = await getHealthGorillaTenantAccountId();
        labOrder.setPerformingLabAccountNumber(labAccountNumber);

        setHasAutoSelectedLab(true);
        if (labCandidate.id) {
          setDefaultLabId(labCandidate.id);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to auto-select default performing lab:', error);
      }
    };

    autoSelectDefaultLab();

    return () => {
      cancelled = true;
    };
  }, [
    labOrder,
    labOrder.state.performingLab,
    state.questionnaire,
    hasAutoSelectedLab,
    getHealthGorillaTenantAccountId,
  ]);

  // Clear form when patient changes
  useEffect(() => {
    if (!patient?.id) {
      return;
    }
    dispatch({ type: 'reset' });
    setHasAutoSelectedLab(false);
    setDefaultLabId(undefined);
    setInitialDefaultTests(undefined);
    setHasUserClearedAllTests(false);
    warnedMissingTestLookupKeysRef.current.clear();
  }, [patient?.id]);

  const handleQuestionnaireChange = useCallback(
    (response: QuestionnaireResponse) => {
      const updatedResponse: QuestionnaireResponse = {
        ...response,
        status: 'in-progress',
        authored: new Date().toISOString(),
        source: createReference(placedBy),
        subject: patient ? createReference(patient) : undefined,
        questionnaire:
          state.questionnaire?.url || (state.questionnaire?.id ? getReferenceString(state.questionnaire) : undefined),
      };
      dispatch({ type: 'setQuestionnaireResponse', response: updatedResponse });
    },
    [patient, placedBy, state.questionnaire]
  );

  const handleLabsChange = useCallback(
    (labs: LabOrganization[]) => {
      const lab = labs[0];
      const previousLabId = labOrder.state.performingLab?.id;

      // If cleared, reset tests and (effectively) clear lab selection
      if (!lab) {
        setHasUserClearedAllTests(false);
        labOrder.setTests([]);
        return;
      }

      // If selecting the same lab again, do nothing
      if (previousLabId && lab.id && previousLabId === lab.id) {
        return;
      }

      // Lab selection changed; allow auto-selection defaults again.
      setHasUserClearedAllTests(false);
      labOrder.setPerformingLab(lab);
      labOrder.setPerformingLabAccountNumber(getBariendoLabCorpAccountNumber());

      // If switching to the default lab and we have stored initial tests, restore them
      if (defaultLabId && initialDefaultTests && lab.id === defaultLabId) {
        labOrder.setTests(initialDefaultTests);
      } else {
        // For any non-default lab, clear tests so the user can pick fresh ones
        labOrder.setTests([]);
      }
    },
    [labOrder, defaultLabId, initialDefaultTests]
  );

  // Auto-select first test group when lab is selected
  useEffect(() => {
    const performingLab = labOrder.state.performingLab;
    const currentTests = labOrder.state.selectedTests;

    // Only auto-select if lab is set, no tests selected yet, and questionnaire is loaded
    if (!performingLab || currentTests.length > 0 || !state.questionnaire || hasUserClearedAllTests) {
      return;
    }

    const autoSelectFirstGroup = async () => {
      setIsAutoSelectingTests(true);
      try {
        const testGroupsItem = state.questionnaire?.item?.find(
          (item) => item.linkId === QUESTIONNAIRE_LINK_IDS.TEST_GROUPS
        );
        const firstTestGroup = testGroupsItem?.answerOption?.[0];

        if (firstTestGroup) {
          const testsExtension = firstTestGroup.extension?.find(
            (ext) => ext.url === BARIENDO_TEST_GROUP_ITEM_EXTENSION
          );
          const testEntries = testsExtension?.valueString?.split(',').map((name) => name.trim()) || [];

          const testsToAdd: TestCoding[] = [];
          for (const entry of testEntries) {
            const { searchName, preferredCode } = parseTestEntry(entry);
            if (!searchName) {
              continue;
            }

            const results = await searchLabOrderTestCodings(
              (query) => labOrder.searchAvailableTests(query),
              searchName,
              preferredCode
            );

            const preferredMatch = preferredCode ? results.find((test) => test.code === preferredCode) : undefined;
            if (preferredMatch && !testsToAdd.some((t) => t.code === preferredMatch.code)) {
              testsToAdd.push(preferredMatch);
              continue;
            }

            const lowerTestName = searchName.toLowerCase().replace(/-/g, ' ');
            const matchingTest = results.find((test) => {
              const testDisplay = test.display?.toLowerCase() || '';
              return testDisplay === lowerTestName || testDisplay.startsWith(lowerTestName);
            });

            if (matchingTest && !testsToAdd.some((t) => t.code === matchingTest.code)) {
              testsToAdd.push(matchingTest);
            } else if (!matchingTest) {
              const warningKey = `${performingLab.id ?? ''}:${searchName}`;
              if (!warnedMissingTestLookupKeysRef.current.has(warningKey)) {
                warnedMissingTestLookupKeysRef.current.add(warningKey);
                alert.warning({
                  title: 'Lab test lookup',
                  message: `No matching lab test was found for "${searchName}". Please, contact the Orbit team.`,
                });
              }
            }
          }

          labOrder.setTests(testsToAdd);

          // Remember the initial tests for the default lab so we can restore them later
          if (performingLab.id && defaultLabId && performingLab.id === defaultLabId && !initialDefaultTests) {
            setInitialDefaultTests(testsToAdd);
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to auto-select first test group:', error);
      } finally {
        setIsAutoSelectingTests(false);
      }
    };

    autoSelectFirstGroup();
  }, [
    labOrder.state.performingLab,
    labOrder.state.selectedTests.length,
    state.questionnaire,
    labOrder,
    defaultLabId,
    initialDefaultTests,
    hasUserClearedAllTests,
  ]);

  const handleTestsChange = useCallback(
    (tests: TestCoding[]) => {
      // If the user clears all tests, don't re-apply defaults automatically.
      setHasUserClearedAllTests(tests.length === 0);
      labOrder.setTests(tests);
    },
    [labOrder]
  );

  const handleSubmit = useEffectEvent(async () => {
    if (!patient || !state.questionnaire || !state.questionnaireResponse) {
      alert.error('Form is not ready');
      return;
    }

    // Validate patient profile conforms to HealthGorilla requirements
    const patientValidation = validatePatientProfile(patient);
    if (!patientValidation.valid) {
      alert.error(`Patient Profile Error: ${patientValidation.error}`);
      return;
    }

    if (!orderingProvider) {
      alert.error('Choose an ordering provider');
      return;
    }

    // Validate practitioner profile conforms to HealthGorilla requirements
    const providerValidation = validateProviderProfile(orderingProvider);
    if (!providerValidation.valid) {
      alert.error(`Practitioner Profile Error: ${providerValidation.error}`);
      return;
    }

    // Validate Health Gorilla order
    const errors = labOrder.validateOrder();
    if (errors) {
      if (errors.performingLab) {
        alert.error(errors.performingLab.message);
        return;
      }
      if (errors.selectedTests) {
        alert.error(errors.selectedTests.message);
        return;
      }
      // Show all error messages if available
      const errorMessages = Object.entries(errors)
        .map(([field, error]) => `${field}: ${(error as { message: string }).message}`)
        .join(', ');
      alert.error(errorMessages || 'Please complete all required fields');
      return;
    }

    let createdBundleResources: Array<{ resourceType: ResourceType; id: string }> = [];
    let healthGorillaOrderSent = false;

    try {
      dispatch({ type: 'setSubmitting', isSubmitting: true });

      // Create Health Gorilla order bundle (includes ServiceRequest, DiagnosticReport, Specimens, etc.)
      const { transactionResponse } = await labOrder.createOrderBundle();
      createdBundleResources = (transactionResponse.entry || [])
        .map((entry) => entry.resource)
        .filter((resource): resource is WithId<Resource> => typeof resource?.id === 'string')
        .map((resource) => ({ resourceType: resource.resourceType, id: resource.id }));

      // Extract all ServiceRequests from transaction response
      const serviceRequests = (transactionResponse.entry || [])
        .filter((entry) => entry.resource?.resourceType === 'ServiceRequest')
        .map((entry) => entry.resource)
        .filter((resource): resource is ServiceRequest => resource !== undefined);

      // Extract main ServiceRequest (the one without basedOn) and child ServiceRequests
      const mainServiceRequest = serviceRequests.find((sr) => !sr.basedOn) || serviceRequests[0];

      if (!mainServiceRequest) {
        throw new Error('No ServiceRequest found in order bundle');
      }

      // Send lab order to Health Gorilla
      await medplum.executeBot(HEALTHGORILLA_SEND_LAB_ORDER_BOT_IDENTIFIER, mainServiceRequest, ContentType.FHIR_JSON);
      healthGorillaOrderSent = true;

      // Query for child ServiceRequests (individual tests)
      const childServiceRequests = await medplum.searchResources('ServiceRequest', {
        'based-on': getReferenceString(mainServiceRequest),
      });

      // Create completed QuestionnaireResponse
      const completedResponse: QuestionnaireResponse = {
        ...state.questionnaireResponse,
        status: 'completed',
        authored: new Date().toISOString(),
        item: withRequesterAnswer(state.questionnaireResponse.item, orderingProvider),
      };

      const createdResponse = await medplum.createResource<QuestionnaireResponse>(completedResponse);

      // Extract lab name and test names for searchable content
      const labName = mainServiceRequest?.performer?.[0]?.display || '';
      const testNames = childServiceRequests
        .map((sr) => sr.code?.coding?.[0]?.display)
        .filter(Boolean)
        .join(', ');

      // Create Communication that references the QuestionnaireResponse and all child ServiceRequests
      const unsecuredCommunication: Communication = {
        resourceType: 'Communication',
        status: 'completed',
        category: [{ coding: [COMMUNICATION_CATEGORY_CODING_MAP[CommunicationCategory.LAB_ORDER]] }],
        subject: createReference(patient),
        sent: new Date().toISOString(),
        // Must stay the signed-in user: LabsWritePolicy only allows lab-order cards with sender=%profile.
        sender: createReference(placedBy),
        basedOn: [createReference(createdResponse), ...childServiceRequests.map((sr) => createReference(sr))],
        payload: [
          {
            contentString: `Lab Order - ${labOrder.state.selectedTests.length} test(s) ordered | Lab: ${labName} | Tests: ${testNames}`,
          },
        ],
      };

      const communication = await medplum.createResource<Communication>(unsecuredCommunication);

      await medplum.updateResource({
        ...communication,
        meta: {
          ...communication.meta,
          security: [RESTRICTED_CONFIDENTIALITY_LEVEL_CODING],
        },
      });

      alert.success('Lab order created successfully');
      onSuccess();

      // Reset form
      dispatch({ type: 'reset' });
    } catch (error) {
      // Roll back resources created by createOrderBundle if we fail before sending the order to Health Gorilla.
      if (!healthGorillaOrderSent && createdBundleResources.length > 0) {
        const resourcesToDelete = [...createdBundleResources].reverse();
        for (const resource of resourcesToDelete) {
          try {
            await medplum.deleteResource(resource.resourceType, resource.id);
          } catch (deleteError) {
            // eslint-disable-next-line no-console
            console.error(
              `Failed to rollback ${resource.resourceType}/${resource.id} after lab order failure`,
              deleteError
            );
          }
        }
      }

      const errorMessage = formatHealthGorillaError(error);
      alert.error(errorMessage);
    } finally {
      dispatch({ type: 'setSubmitting', isSubmitting: false });
    }
  });

  return {
    labOrder,
    questionnaire: state.questionnaire,
    questionnaireResponse: state.questionnaireResponse,
    isSubmitting: state.isSubmitting,
    isLoadingQuestionnaire: state.isLoadingQuestionnaire,
    isAutoSelectingTests,
    handleQuestionnaireChange,
    handleLabsChange,
    handleTestsChange,
    handleSubmit,
  };
}
